/** @jsxImportSource preact */
import * as m from "../../paraglide/messages.js";
import { CARD_STYLES } from "../form/card-styles";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { InfoIcon, PaperPlaneIcon } from "../icons";
import type { WidgetTokens } from "../theme";
import { CARD_PARTS_STYLES, FactRow, Facts } from "./card-parts";
import { EMAIL_PREVIEW_STYLES, EmailPreview } from "./email-preview";
import type { WidgetEmailPreview } from "./types";

export interface WidgetReviewRequest {
  /**
   * 将发给商家的整封邮件。摘要里的 To（商家邮箱）和 Contact email（买家邮箱）
   * 取自它的 to 和 replyTo，不另给一份，免得两处对不上。
   */
  preview: WidgetEmailPreview;
  /** 请求类型的名字，如 `Order cancellation`，原文由后端给。 */
  requestType: string;
}

export interface ReviewCardProps {
  /** 邮件区最高多少像素，超出在卡内滚动；默认 240。给足够大的值就是完整展开。 */
  emailMaxHeight?: number;
  disabled?: boolean;
  /** Edit details：回到采集卡继续改。 */
  onEdit?: () => void;
  /** Not now：暂停请求并收起浮层，消息里留 Contact store 入口。 */
  onNotNow?: () => void;
  /** Send request。 */
  onSend?: () => void;
  request: WidgetReviewRequest;
  tokens: WidgetTokens;
}

/** 邮件区默认最高这么多，超出在卡内滚动。 */
const EMAIL_MAX_HEIGHT = 240;

export const REVIEW_CARD_STYLES = `${CARD_PARTS_STYLES}${EMAIL_PREVIEW_STYLES}
.gn-card .gn-review-body { gap:12px; --gn-facts-gap:10px; --gn-fact-label:96px; }
.gn-review-email { overflow-y:auto; overscroll-behavior:contain; border-radius:var(--gn-form-r-well); scrollbar-width:thin; scrollbar-color:var(--gn-form-line) transparent; }
.gn-review-email:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; }
.gn-review-note { display:flex; align-items:flex-start; gap:6px; padding:0 4px; font-size:12px; line-height:1.45; color:var(--gn-form-quiet); }
.gn-review-note > svg { flex:none; margin-top:2px; color:var(--gn-form-faint); }
.gn-review-actions { display:flex; gap:8px; }
.gn-review-actions > .gn-btn { flex:1 1 0; min-width:0; }
`;

/**
 * 发送前的确认卡（Review your request）：摘要三行（To、Contact email、Request type）、
 * 卡内可滚动的整封邮件、一句「商家会收到什么、能直接回复你」的说明，
 * 底部墨黑的 Send request，其下并排白底的 Edit details 和无底的 Not now。
 */
export function ReviewCard({
  emailMaxHeight = EMAIL_MAX_HEIGHT,
  disabled,
  onEdit,
  onNotNow,
  onSend,
  request,
  tokens: t,
}: ReviewCardProps) {
  const { preview } = request;
  return (
    <div
      aria-label={m.widget_review_title()}
      class="gn-card"
      role="group"
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${REVIEW_CARD_STYLES}`}</style>
      <div class="gn-card-head">
        <div class="gn-card-title">{m.widget_review_title()}</div>
      </div>
      <div class="gn-card-body gn-review-body">
        <div class="gn-well" data-roomy="">
          <Facts>
            <FactRow label={m.widget_review_to()} value={preview.to} />
            <FactRow
              label={m.widget_review_contact_email()}
              value={preview.replyTo}
            />
            <FactRow
              label={m.widget_review_request_type()}
              value={request.requestType}
            />
          </Facts>
        </div>
        <div
          aria-label={m.widget_review_full_request()}
          class="gn-review-email"
          role="region"
          style={{ maxHeight: emailMaxHeight }}
          tabIndex={0}
        >
          <EmailPreview preview={preview} tokens={t} />
        </div>
        <div class="gn-review-note">
          <InfoIcon size={13} />
          <div>{m.widget_review_info()}</div>
        </div>
      </div>
      <div class="gn-card-foot">
        <FormButton disabled={disabled} fullWidth onClick={onSend} tokens={t}>
          <PaperPlaneIcon size={14} />
          {m.widget_review_send()}
        </FormButton>
        <div class="gn-review-actions">
          <FormButton
            disabled={disabled}
            onClick={onEdit}
            tokens={t}
            variant="secondary"
          >
            {m.widget_review_edit()}
          </FormButton>
          <FormButton
            disabled={disabled}
            onClick={onNotNow}
            tokens={t}
            variant="ghost"
          >
            {m.widget_review_not_now()}
          </FormButton>
        </div>
      </div>
    </div>
  );
}
